import { ReactElement } from 'react';

import { formatCompactNumber } from '@occc-app/lib/numbers';
import { GoalProgress } from '@occc/graphql';
import { Typography } from '@occc/ui-design-system';

type NarrativeTextBoxColorVariant = 'warning' | 'success';

export type NarrativeTextParams = {
  narrativeTextBoxColorVariant: NarrativeTextBoxColorVariant;
  narrativeTextTitle: string;
  narrativeTextMembershipDescription: ReactElement;
  narrativeTextTransactionsDescription: ReactElement;
  isGoalComplete: boolean;
};

const HighlightedText = ({ children }: { children: ReactElement | string }) => (
  <Typography component='span' fontWeight='bold' color='textSecondary' variant='body2'>
    {` ${children} `}
  </Typography>
);

const getPerformanceBelowTransactionsGoalText = (
  progress: number,
  goal: number,
  daysLeft?: number,
) => (
  <Typography marginTop={1} color='textSecondary' variant='body2'>
    You are currently
    <HighlightedText>under your Transaction goal</HighlightedText>
    with progress of
    <HighlightedText>{`${formatCompactNumber(progress)} transactions`}</HighlightedText>
    versus a goal of
    <HighlightedText>{`${formatCompactNumber(goal)} transactions.`}</HighlightedText>
    You need to average
    <HighlightedText>{`${Math.ceil(
      (goal - progress) / (daysLeft || 1),
    )} transactions/day`}</HighlightedText>
    for the rest of the month to meet your goal.
  </Typography>
);

const getExpectedPerformanceTransactionsGoalText = (progress: number, goal: number) => (
  <Typography marginTop={1} color='textSecondary' variant='body2'>
    You are currently
    <HighlightedText>on pace to meet your Transactions goal</HighlightedText>
    with progress of
    <HighlightedText>{`${formatCompactNumber(progress)} transactions`}</HighlightedText>
    versus a goal of
    <HighlightedText>{`${formatCompactNumber(goal)} transactions.`}</HighlightedText>
  </Typography>
);

const getPerformanceBelowMembershipGoalText = (
  progress: number,
  goal: number,
  daysLeft?: number,
) => (
  <Typography marginTop={1} color='textSecondary' variant='body2'>
    You are currently
    <HighlightedText>under your New Member goal</HighlightedText>
    with progress of
    <HighlightedText>{`${formatCompactNumber(progress)} new members`}</HighlightedText>
    against a goal of
    <HighlightedText>{`${formatCompactNumber(goal)} members.`}</HighlightedText>
    You need to average
    <HighlightedText>{`${Math.ceil(
      (goal - progress) / (daysLeft || 1),
    )} members/day`}</HighlightedText>
    for the rest of the month to meet your goal.
  </Typography>
);

const getExpectedPerformanceMembershipGoalText = (progress: number, goal: number) => (
  <Typography marginTop={1} color='textSecondary' variant='body2'>
    You are currently
    <HighlightedText>on pace to meet your New Member goal</HighlightedText>
    with progress of
    <HighlightedText>{`${formatCompactNumber(progress)} new members`}</HighlightedText>
    against a goal of
    <HighlightedText>{`${formatCompactNumber(goal)} members.`}</HighlightedText>
  </Typography>
);

export const getNarrativeTextData = (
  membersProgress: GoalProgress,
  transactionProgress: GoalProgress,
  daysLeft?: number,
): NarrativeTextParams => {
  const areBothBelowGoal =
    membersProgress.progress < membersProgress.goal &&
    transactionProgress.progress < transactionProgress.goal;
  const areBothCompletedGoal =
    membersProgress.progress >= membersProgress.goal &&
    transactionProgress.progress >= transactionProgress.goal;
  const areMembershipBelowGoal =
    !areBothBelowGoal && !areBothCompletedGoal && membersProgress.progress < membersProgress.goal;

  if (areBothBelowGoal) {
    return {
      isGoalComplete: false,
      narrativeTextBoxColorVariant: 'warning',
      narrativeTextTitle: 'You are currently under your New Member Goal and Transactions goal',
      narrativeTextMembershipDescription: getPerformanceBelowMembershipGoalText(
        membersProgress.progress,
        membersProgress.goal,
        daysLeft,
      ),
      narrativeTextTransactionsDescription: getPerformanceBelowTransactionsGoalText(
        transactionProgress.progress,
        transactionProgress.goal,
        daysLeft,
      ),
    };
  }

  if (areBothCompletedGoal) {
    return {
      isGoalComplete: true,
      narrativeTextBoxColorVariant: 'success',
      narrativeTextTitle:
        'You are currently on pace for your New Member Goal and your Transactions goal',
      narrativeTextMembershipDescription: getExpectedPerformanceMembershipGoalText(
        membersProgress.progress,
        membersProgress.goal,
      ),
      narrativeTextTransactionsDescription: getExpectedPerformanceTransactionsGoalText(
        transactionProgress.progress,
        transactionProgress.goal,
      ),
    };
  }

  if (areMembershipBelowGoal) {
    return {
      isGoalComplete: false,
      narrativeTextBoxColorVariant: 'warning',
      narrativeTextTitle:
        'You are currently under your New Member Goal and on pace for your Transactions goal',
      narrativeTextMembershipDescription: getPerformanceBelowMembershipGoalText(
        membersProgress.progress,
        membersProgress.goal,
        daysLeft,
      ),
      narrativeTextTransactionsDescription: getExpectedPerformanceTransactionsGoalText(
        transactionProgress.progress,
        transactionProgress.goal,
      ),
    };
  }

  return {
    isGoalComplete: false,
    narrativeTextBoxColorVariant: 'warning',
    narrativeTextTitle:
      'You are currently on pace for your New Member Goal and under your Transactions goal',
    narrativeTextMembershipDescription: getExpectedPerformanceMembershipGoalText(
      membersProgress.progress,
      membersProgress.goal,
    ),
    narrativeTextTransactionsDescription: getPerformanceBelowTransactionsGoalText(
      transactionProgress.progress,
      transactionProgress.goal,
      daysLeft,
    ),
  };
};

export const calculatePercentOfNumber = (currentValue: number, maxValue: number) =>
  (currentValue / maxValue) * 100;
